import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Grid,
  Paper,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme
} from '@mui/material';
import Switch from '@mui/material/Switch';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import { Design, DesignMaterialAllocated } from '../api/design';
import api from '../services/api';
import designApi from '../api/design';
import { productionApi, repairsApi } from '../api/production';
import { Stepper, Step, StepLabel, Chip, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import { designBomApi, DesignBOMItem } from '../api/designBom';
import { productionOrdersApi, ProductionOrder } from '../api/productionOrders';

interface SizeSummary {
  size: string;
  totalCut: number;
  stitchingIssued: number; // main issued (non-refinish)
  stitchingDefects: number; // defects from main receives
  stitchingReceived: number; // successful receives (non-defect) for main
  stitchingRemaining: number; // remaining across main + refinish balance
  stitchingRefinish: number; // refinish issued
  stitchingRefinishReceived: number; // refinish received (incl. defects)
  pressIssued: number;
  pressReceived: number;
  pressRemaining: number;
}

export interface DesignPreviewDialogProps {
  open: boolean;
  design?: Design | null;
  previewImage?: string | null;
  designNo?: string;
  topDesign?: any;
  bottomDesign?: any;
  loadingCuttingData?: boolean;
  onClose: () => void;
  onStatusChange?: (designId: number, newStatus: string) => void;
  hideCost?: boolean;
  // New props for stitching completion
  designCuttingId?: number;
  showCompletionForm?: boolean;
  onComplete?: () => void;
}

const DesignPreviewDialog: React.FC<DesignPreviewDialogProps> = ({ 
  open, 
  design, 
  previewImage,
  designNo,
  topDesign,
  bottomDesign,
  loadingCuttingData = false,
  onClose,
  onStatusChange,
  hideCost = false
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [topSizeSummary, setTopSizeSummary] = useState<SizeSummary[]>([]);
  const [bottomSizeSummary, setBottomSizeSummary] = useState<SizeSummary[]>([]);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState<'APPROVED' | 'REJECTED' | 'HOLD' | 'COMPLETE'>('HOLD');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [statusSuccess, setStatusSuccess] = useState<string | null>(null);
  const [allocatedMaterials, setAllocatedMaterials] = useState<DesignMaterialAllocated[]>([]);
  const [jobWorkMaterials, setJobWorkMaterials] = useState<any[]>([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [materialsError, setMaterialsError] = useState<string | null>(null);
  const [jobWorks, setJobWorks] = useState<any[]>([]);
  const [jobWorksLoading, setJobWorksLoading] = useState(false);
  const [allJobWorks, setAllJobWorks] = useState<any[]>([]); // includes inhouse for stage tracking
  // Dispatch section removed per request
  
  // BOM state
  const [bomItems, setBomItems] = useState<DesignBOMItem[]>([]);
  const [bomLoading, setBomLoading] = useState(false);
  const isBomFinalized = bomItems.some(b => b.finalized_at);
  const bomEstimatedCost = bomItems.reduce((sum, item) => sum + (item.consumption_per_piece * (item.estimated_rate || 0)), 0);

  // Purchase Orders state
  const [purchaseOrders, setPurchaseOrders] = useState<any[]>([]);
  const [purchaseOrdersLoading, setPurchaseOrdersLoading] = useState(false);

  // Production Orders state
  const [productionOrders, setProductionOrders] = useState<ProductionOrder[]>([]);
  const [productionOrdersLoading, setProductionOrdersLoading] = useState(false);

  // Cost calculation state
  const [stitchingData, setStitchingData] = useState<any[]>([]);
  const [stitchingLoading, setStitchingLoading] = useState(false);
  const [batches, setBatches] = useState<any[]>([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [repairItems, setRepairItems] = useState<any[]>([]);
  const [repairsLoading, setRepairsLoading] = useState(false);
  const [summaryData, setSummaryData] = useState<{
    inhouseStitchingIssued: number;
    cuttingQty: number;
    stitchedPieces: number;
    stitchingDefects: number;
    pressedPieces: number;
    pressDefects: number;
    barcoded: number;
    inRepair: number;
    permanentDamage: number;
    pending: number;
    status: 'ok' | 'needs_repair' | 'error' | null;
    statusMessage: string;
  } | null>(null);

  // Responsive design
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { canView } = usePermissions();
  
  // Check if user has permission to view cost breakdown
  const canViewCost = canView('design-cost');
  
  // If we have a design object, use that, otherwise use the previewImage
  const images = design 
    ? [
        design.image_url,
        design.image2_url,
        design.image3_url,
        design.image4_url,
        design.dupatta_image_ref
      ].filter(Boolean) as string[] 
    : previewImage 
      ? [previewImage].filter(Boolean) as string[]
      : [];
  
  // Helper function to calculate summary data
  const calculateSummaryData = useCallback((
    batchesArray: any[], 
    jobWorksArray: any[], 
    topDesignCutting: any, 
    stitchingAssignments: any[] = [],
    repairItemsArray: any[] = []
  ) => {
    // Calculate cutting quantity from design cutting table for TOP garment part
    let cuttingQty = 0;
    if (topDesignCutting) {
      // Use total_pieces if available, otherwise sum all size pieces
      if (topDesignCutting.total_pieces) {
        cuttingQty = topDesignCutting.total_pieces;
      } else {
        cuttingQty = (topDesignCutting.xs_pieces || 0) +
                     (topDesignCutting.s_pieces || 0) +
                     (topDesignCutting.m_pieces || 0) +
                     (topDesignCutting.l_pieces || 0) +
                     (topDesignCutting.xl_pieces || 0) +
                     (topDesignCutting.xxl_pieces || 0) +
                     (topDesignCutting.three_xl_pieces || 0);
      }
    }
    
    // Calculate stitching defects from worker receives (sum of defect_quantity from stitching receives)
    // Only count defects from main stitching jobs (not refinish) for TOP garment part only
    // Filter by jobType (can be 'STITCHING' or check if assignment has stitching-related jobType)
    const mainStitchingAssignments = stitchingAssignments.filter((a: any) => {
      const jobType = a.jobType || a.job_type || '';
      const isStitching = jobType.toUpperCase().includes('STITCHING');
      const isTop = (a.garment_part || '').toUpperCase() === 'TOP';
      return isStitching && !a.is_refinish_job && isTop;
    });
    const stitchingDefects = mainStitchingAssignments.reduce((sum: number, assignment: any) => {
      if (assignment.receives && Array.isArray(assignment.receives)) {
        const defects = assignment.receives.reduce((rSum: number, receive: any) => {
          return rSum + (Number(receive.defect_quantity) || 0);
        }, 0);
        return sum + defects;
      }
      return sum;
    }, 0);

    // Stitched pieces (received good qty from TOP stitching assignments)
    const stitchedPieces = mainStitchingAssignments.reduce((sum: number, assignment: any) => {
      if (assignment.receives && Array.isArray(assignment.receives)) {
        return sum + assignment.receives.reduce((rSum: number, r: any) => rSum + (Number(r.quantity_received) || 0), 0);
      }
      return sum;
    }, 0);

    // Press assignments for TOP garment part
    const mainPressAssignments = stitchingAssignments.filter((a: any) => {
      const jobType = a.jobType || a.job_type || '';
      const isPress = jobType.toUpperCase().includes('PRESS');
      const isTop = (a.garment_part || '').toUpperCase() === 'TOP';
      return isPress && isTop;
    });

    const pressedPieces = mainPressAssignments.reduce((sum: number, assignment: any) => {
      if (assignment.receives && Array.isArray(assignment.receives)) {
        return sum + assignment.receives.reduce((rSum: number, r: any) => rSum + (Number(r.quantity_received) || 0), 0);
      }
      return sum;
    }, 0);

    const pressDefects = mainPressAssignments.reduce((sum: number, assignment: any) => {
      if (assignment.receives && Array.isArray(assignment.receives)) {
        return sum + assignment.receives.reduce((rSum: number, r: any) => rSum + (Number(r.defect_quantity) || 0), 0);
      }
      return sum;
    }, 0);

    let barcoded = 0;
    let permanentDamage = 0;
    let totalIssued = 0;

    // Only count batches where garment_part is TOP or blank/null
    const topBatches = batchesArray.filter((batch: any) => {
      const gp = (batch.garment_part || '').toUpperCase();
      return gp === 'TOP' || gp === '';
    });

    topBatches.forEach((batch: any) => {
      if (batch.size_breakdown && Array.isArray(batch.size_breakdown)) {
        batch.size_breakdown.forEach((sb: any) => {
          barcoded += sb.barcoded_qty || 0;
          permanentDamage += sb.damaged_qty || 0;
          totalIssued += sb.issued_qty || batch.total_issued || 0;
        });
      } else {
        // Fallback if size_breakdown is not available
        totalIssued += batch.total_issued || 0;
        barcoded += batch.total_barcoded || 0;
        permanentDamage += batch.total_damaged || 0;
      }
    });

    // In Repair: sum repair quantities for this design across all sizes
    // Use repair items directly (PENDING + IN_PROGRESS), matching Repair Queue logic
    const inRepair = (repairItemsArray || []).reduce((sum: number, item: any) => {
      return sum + (Number(item.quantity) || 0);
    }, 0);
    
    // Calculate inhouse stitching issued from job works
    // Use designDetails pieces (quantity given in program), not received qty
    const inhouseStitchingIssued = (jobWorksArray || []).reduce((sum: number, jw: any) => {
      const isInhouse = jw.vendor?.is_internal || 
                       (jw.vendor?.registered_name || '').toLowerCase().includes('inhouse') ||
                       (jw.jobWorkerName || '').toLowerCase().includes('inhouse');
      const jobTypeName = (jw.jobType?.name || jw.jobType || '').toUpperCase();
      const isStitching = jobTypeName.includes('STITCHING');
      if (isInhouse && isStitching && jw.status !== 'CANCELLED') {
        if (Array.isArray(jw.designDetails) && jw.designDetails.length > 0) {
          const pieces = jw.designDetails.reduce((dSum: number, detail: any) => {
            return dSum + (Number(detail.pieces) || 0);
          }, 0);
          return sum + pieces;
        }
        // Fallback to qty if designDetails are missing
        return sum + (jw.qty || 0);
      }
      return sum;
    }, 0);
    
    // Calculate pending based on cutting quantity (minus defects) minus what's been accounted for
    // Pending = (Cutting Quantity - Stitching Defects) - Barcoded - Permanent Damage - In Repair
    const effectiveCuttingQty = cuttingQty - stitchingDefects;
    const completed = barcoded + permanentDamage;
    const pending = Math.max(0, effectiveCuttingQty - completed - inRepair);
    
    // Calculate production status based on cutting quantity relationship
    let status: 'ok' | 'needs_repair' | 'error' | null = null;
    let statusMessage = '';
    
    const totalAccounted = barcoded + permanentDamage + inRepair;
    
    if (cuttingQty > 0) {
      // Green: Production is OK if barcoded equals or exceeds cutting quantity (after deducting defects)
      // OR if cutting quantity equals barcoded + permanent damage
      const effectiveCuttingQty = cuttingQty - stitchingDefects;
      if (barcoded >= effectiveCuttingQty || cuttingQty === barcoded + permanentDamage) {
        status = 'ok';
        statusMessage = 'Production OK - All pieces accounted for';
      } else if (cuttingQty === totalAccounted && inRepair > 0) {
        // Yellow: Needs repair done quickly
        status = 'needs_repair';
        statusMessage = 'Needs repair - Complete repairs quickly';
      } else {
        // Red: Quantity mismatch - something is wrong
        status = 'error';
        statusMessage = 'Quantity mismatch - Check quantities';
      }
    }
    
    setSummaryData({
      inhouseStitchingIssued,
      cuttingQty,
      stitchedPieces,
      stitchingDefects,
      pressedPieces,
      pressDefects,
      barcoded,
      inRepair,
      permanentDamage,
      pending,
      status,
      statusMessage
    });
  }, []);

  useEffect(() => {
    if (open && designNo) {
      setJobWorksLoading(true);
      // dispatches removed
      setStitchingLoading(true);
      
      api.get(`/job-works/by-design/${designNo}`)
        .then(res => {
          console.log('=== JOB WORKS API SUCCESS ===');
          console.log('Design No:', designNo);
          console.log('Response data:', res.data);
          console.log('Data length:', res.data.length);
          // Exclude only CANCELLED; include internal/inhouse stitching again per request
          const all = Array.isArray(res.data)
            ? res.data.filter((jw: any) => jw.status !== 'CANCELLED')
            : [];
          setAllJobWorks(all);
          setJobWorks(all);
          // Collect job-work materials with material details
          const jwMats = all.flatMap((jw: any) => Array.isArray(jw.materials) ? jw.materials : []);
          setJobWorkMaterials(jwMats);
          
        })
        .catch(err => {
          console.error('=== JOB WORKS API ERROR ===');
          console.error('Design No:', designNo);
          console.error('Error:', err);
          console.error('Error response:', err.response?.data);
          console.error('Status:', err.response?.status);
          if (err.response?.status === 403) {
            console.warn('Permission denied for job-works. User may not have required permissions.');
          }
          setJobWorks([]);
        })
        .finally(() => setJobWorksLoading(false));
      
      // Fetch production batches for summary table
      setBatchesLoading(true);
      productionApi.getAll({ design_no: designNo })
        .then((data) => {
          const batchesArray = Array.isArray(data) ? data : [];
          setBatches(batchesArray);
        })
        .catch((err) => {
          console.error('Error fetching production batches:', err);
          if (err.response?.status === 403) {
            console.warn('Permission denied for production batches. User may not have required permissions.');
          }
          setBatches([]);
          setSummaryData(null);
        })
        .finally(() => setBatchesLoading(false));
      
      // Fetch repair items for this design to compute in-repair quantity
      setRepairsLoading(true);
      repairsApi.getQueue({})
        .then((allRepairs) => {
          // Use only non-completed repairs for this design number
          const activeRepairs = (allRepairs || []).filter((r: any) => 
            r.design_no === designNo && r.repair_status !== 'COMPLETED'
          );
          setRepairItems(activeRepairs);
        })
        .catch((err) => {
          console.error('Error fetching repair items for design preview:', err);
          setRepairItems([]);
        })
        .finally(() => setRepairsLoading(false));
      
      // Fetch stitching/worker assignment data for cost calculations
      api.get('/worker-assignments')
        .then(res => {
          const assignments = res.data.filter((assignment: any) =>
            assignment.design_no === designNo
          );
          setStitchingData(assignments);
        })
        .catch((err) => {
          console.error('Error fetching worker assignments:', err);
          if (err.response?.status === 403) {
            console.warn('Permission denied for worker-assignments. User may not have "assignments" view permission.');
          }
          setStitchingData([]);
        })
        .finally(() => setStitchingLoading(false));

      // Fetch purchase orders tagged with this design
      setPurchaseOrdersLoading(true);
      api.get('/purchase-orders', { params: { design_no: designNo, pageSize: 100 } })
        .then(res => {
          const data = res.data;
          const orders = Array.isArray(data) ? data : (data?.data || []);
          setPurchaseOrders(orders);
        })
        .catch(() => setPurchaseOrders([]))
        .finally(() => setPurchaseOrdersLoading(false));

      // Fetch production orders for this design
      setProductionOrdersLoading(true);
      productionOrdersApi.getByDesign(designNo)
        .then(orders => setProductionOrders(Array.isArray(orders) ? orders : []))
        .catch(() => setProductionOrders([]))
        .finally(() => setProductionOrdersLoading(false));
    }
  }, [open, designNo]);

  const fetchSizeSummary = useCallback(async (designNumber: string) => {
    try {
      setLoadingSummary(true);
      
      // Get all worker assignments for this design
      const response = await api.get('/worker-assignments');
      const assignments = response.data || [];
      
      // Filter assignments for this design number
      const designAssignments = assignments.filter((assignment: any) => 
        assignment.design_no === designNumber
      );
      
      // Process top part assignments
      const topAssignments = designAssignments.filter((assignment: any) => 
        assignment.garment_part === 'TOP'
      );
      
      // Process bottom part assignments
      const bottomAssignments = designAssignments.filter((assignment: any) => 
        assignment.garment_part === 'BOTTOM'
      );
      
      // Calculate TOP size summary
      const topSizes = calculateSizeSummary(topAssignments, topDesign);
      setTopSizeSummary(topSizes);
      
      // Calculate BOTTOM size summary
      const bottomSizes = calculateSizeSummary(bottomAssignments, bottomDesign);
      setBottomSizeSummary(bottomSizes);
    } catch (error: any) {
      console.error('Error fetching size summary:', error);
      if (error.response?.status === 403) {
        console.warn('Permission denied for worker-assignments in size summary. User may not have "assignments" view permission.');
      }
      // Set empty summaries on error
      setTopSizeSummary([]);
      setBottomSizeSummary([]);
    } finally {
      setLoadingSummary(false);
    }
  }, [topDesign, bottomDesign]);

  // Recalculate summary when batches, job works, topDesign, or stitchingData changes
  useEffect(() => {
    if (batches.length >= 0 && allJobWorks.length >= 0) {
      calculateSummaryData(batches, allJobWorks, topDesign, stitchingData, repairItems);
    }
  }, [batches, allJobWorks, topDesign, stitchingData, repairItems, calculateSummaryData]);

  // Fetch size summary when cutting data is available
  useEffect(() => {
    if (open && designNo && (topDesign || bottomDesign) && !loadingCuttingData) {
      fetchSizeSummary(designNo);
    }
  }, [open, designNo, topDesign, bottomDesign, loadingCuttingData, fetchSizeSummary]);

  // Initialize approval status when design changes
  useEffect(() => {
    if (design && design.approval_status) {
      setApprovalStatus(design.approval_status as 'APPROVED' | 'REJECTED' | 'HOLD' | 'COMPLETE');
    }
  }, [design]);

  useEffect(() => {
    // Use designNo prop as fallback if design object is not available
    const designNumber = design?.design_no || designNo;
    if (open && designNumber) {
      setLoadingMaterials(true);
      setMaterialsError(null);
      designApi.getAllocatedMaterials(designNumber)
        .then(res => {
          // Handle both response.data and direct array response
          const materials = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
          console.log('Allocated materials fetched:', materials.length, 'items');
          setAllocatedMaterials(materials);
        })
        .catch((err: any) => {
          console.error('Error fetching allocated materials:', err);
          console.error('Error response:', err.response?.data);
          if (err.response?.status === 403) {
            setMaterialsError('Permission denied: You do not have permission to view allocated materials');
          } else {
            setMaterialsError('Failed to load allocated materials');
          }
          setAllocatedMaterials([]);
        })
        .finally(() => setLoadingMaterials(false));
    } else {
      setAllocatedMaterials([]);
    }
  }, [open, design, designNo]);

  // Fetch BOM items
  useEffect(() => {
    const designNumber = design?.design_no || designNo;
    if (open && designNumber) {
      setBomLoading(true);
      designBomApi.getByDesign(designNumber)
        .then(data => {
          setBomItems(data.bomItems || []);
        })
        .catch((err: any) => {
          if (err.response?.status !== 404) {
            console.error('Failed to fetch BOM items:', err);
          }
          setBomItems([]);
        })
        .finally(() => setBomLoading(false));
    } else {
      setBomItems([]);
    }
  }, [open, design, designNo]);

  const calculateSizeSummary = (assignments: any[], designData: any): SizeSummary[] => {
    if (!designData) return [];
    const sizes = [
      { key: 'xs_pieces', label: 'XS' },
      { key: 's_pieces', label: 'S' },
      { key: 'm_pieces', label: 'M' },
      { key: 'l_pieces', label: 'L' },
      { key: 'xl_pieces', label: 'XL' },
      { key: 'xxl_pieces', label: 'XXL' },
      { key: 'three_xl_pieces', label: '3XL' },
    ];
    return sizes
      .filter(size => (designData[size.key] || 0) > 0)
      .map(size => {
        const totalCut = designData[size.key] || 0;
        // Split assignments by job type
        const stitchingAssignments = assignments.filter((a: any) => a.size === size.label && a.jobType === 'STITCHING');
        const pressAssignments = assignments.filter((a: any) => a.size === size.label && a.jobType === 'PRESS');
        // STITCHING (separate defects from main, and track refinish separately)
        const mainAssignments = stitchingAssignments.filter((a: any) => !a.is_refinish_job);
        const refinishAssignments = stitchingAssignments.filter((a: any) => a.is_refinish_job);

        const stitchingIssued = mainAssignments.reduce((sum: number, a: any) => sum + (Number(a.quantity_issued) || 0), 0);
        const stitchingDefects = mainAssignments.reduce((sum: number, a: any) => sum + (a.receives?.reduce((rSum: number, r: any) => rSum + (Number(r.defect_quantity) || 0), 0) || 0), 0);
        const stitchingReceived = mainAssignments.reduce((sum: number, a: any) => sum + (a.receives?.reduce((rSum: number, r: any) => rSum + (Number(r.quantity_received) || 0), 0) || 0), 0);

        const stitchingRefinish = refinishAssignments.reduce((sum: number, a: any) => sum + (Number(a.quantity_issued) || 0), 0);
        const stitchingRefinishReceived = refinishAssignments.reduce((sum: number, a: any) => sum + (a.receives?.reduce((rSum: number, r: any) => rSum + (Number(r.quantity_received) || 0) + (Number(r.defect_quantity) || 0), 0) || 0), 0);

        // Balance remaining across main + refinish
        const totalIssuedAll = (Number(stitchingIssued) || 0) + (Number(stitchingRefinish) || 0);
        const totalReceivedAll = (Number(stitchingReceived) || 0) + (Number(stitchingDefects) || 0) + (Number(stitchingRefinishReceived) || 0);
        const stitchingRemaining = Math.max(0, totalIssuedAll - totalReceivedAll);
        // PRESS
        const pressIssued = pressAssignments.reduce((sum: number, a: any) => sum + a.quantity_issued, 0);
        const pressReceived = pressAssignments.reduce((sum: number, a: any) => sum + (a.receives?.reduce((rSum: number, r: any) => rSum + r.quantity_received + r.defect_quantity, 0) || 0), 0);
        const pressRemaining = stitchingReceived - pressIssued;
        return {
          size: size.label,
          totalCut,
          stitchingIssued,
          stitchingDefects,
          stitchingReceived,
          stitchingRemaining,
          stitchingRefinish,
          stitchingRefinishReceived,
          pressIssued,
          pressReceived,
          pressRemaining,
        };
      });
  };
  
  const handleNext = () => {
    setActiveStep((prevStep) => (prevStep + 1) % images.length);
  };
  
  const handleBack = () => {
    setActiveStep((prevStep) => (prevStep - 1 + images.length) % images.length);
  };
  
  // Handle approval status change
  const handleApprovalStatusChange = async (newStatus: 'APPROVED' | 'REJECTED' | 'HOLD' | 'COMPLETE') => {
    if (!design || approvalStatus === newStatus) return;
    
    setUpdatingStatus(true);
    setStatusError(null);
    
    try {
      await designApi.update(design.id, { approval_status: newStatus });
      setApprovalStatus(newStatus);
      setStatusSuccess(`Design status updated to ${newStatus.toLowerCase()}`);
      
      // Call the callback if provided
      if (onStatusChange) {
        onStatusChange(design.id, newStatus);
      }
    } catch (error) {
      console.error('Error updating design status:', error);
      setStatusError('Failed to update design status. Please try again.');
    } finally {
      setUpdatingStatus(false);
    }
  };
  
  // Determine which title to show
  const title = design ? design.design_no : designNo || 'Design Preview';
  
  // Check if we have any data to display
  const hasNoData = !design && !previewImage && !topDesign && !bottomDesign;
  if (hasNoData) {
    return null;
  }
  
  // Cost calculation function
  const calculateCosts = () => {
    let materialCost = 0;
    let stitchingCost = 0;
    let pressCost = 0;
    let jobWorkCost = 0;

    // ── Material Costs ──
    // Build a map of material IDs from job work materials (actual consumption).
    const jwMaterialMap = new Map<number, { qty: number; rate: number; type: string; code: string }>();

    jobWorkMaterials.forEach((jwm: any) => {
      if (!jwm?.material?.id) return;
      const matId = jwm.material.id;
      const qty = Number(jwm.quantity) || 0;
      const rate = Number(jwm.material?.rate) || 0;
      const existing = jwMaterialMap.get(matId);
      if (existing) {
        existing.qty += qty;
      } else {
        jwMaterialMap.set(matId, {
          qty,
          rate,
          type: jwm.material?.material_type || 'Unknown',
          code: jwm.material?.material_code || ''
        });
      }
    });

    const materialCostsArr: Array<{ type: string; code: string; qty: number; rate: number; total: number; source: string }> = [];
    const processedMaterialIds = new Set<number>();

    // For materials in BOTH job work and allocations, use max qty
    jwMaterialMap.forEach((jwMat, matId) => {
      processedMaterialIds.add(matId);
      const allocation = allocatedMaterials.find(m => m.material_id === matId);
      const allocatedQty = allocation ? (allocation.allocated_qty || 0) : 0;
      const qty = Math.max(jwMat.qty, allocatedQty);
      const total = qty * jwMat.rate;
      materialCost += total;
      materialCostsArr.push({
        type: jwMat.type === 'SEMI_FINISHED' ? 'Semi Finished' : jwMat.type,
        code: jwMat.code,
        qty,
        rate: jwMat.rate,
        total,
        source: allocatedQty > jwMat.qty ? 'Allocation' : 'Job Work'
      });
    });

    // Add allocated materials NOT covered by job work materials
    allocatedMaterials.forEach(mat => {
      if ((mat.allocated_qty || 0) <= 0) return;
      if (processedMaterialIds.has(mat.material_id)) return;
      const rate = mat.material?.rate || 0;
      const total = mat.allocated_qty * rate;
      materialCost += total;
      const matType = mat.material?.material_type || 'Unknown';
      materialCostsArr.push({
        type: matType === 'SEMI_FINISHED' ? 'Semi Finished' : matType,
        code: mat.material?.material_code || '',
        qty: mat.allocated_qty,
        rate,
        total,
        source: 'Allocation'
      });
    });

    // ── Stitching Costs (consolidated by garment part, using quantity_received) ──
    const stitchingAssignments = stitchingData.filter((assignment: any) => assignment.jobType === 'STITCHING');
    const stitchingByPart: { [key: string]: { totalPieces: number, totalCost: number, rateSet: boolean } } = {};
    let hasStitchingRateWarning = false;

    stitchingAssignments.forEach((assignment: any) => {
      const garmentPart = assignment.garment_part || 'UNKNOWN';
      // Use received qty (good + defects) instead of issued qty for accurate costing
      const receivedPieces = (assignment.receives || []).reduce(
        (sum: number, r: any) => sum + (r.quantity_received || 0) + (r.defect_quantity || 0), 0
      );
      const pieces = receivedPieces > 0 ? receivedPieces : (assignment.quantity_issued || 0);
      const rate = Number(assignment.stitching_rate) || 0;
      const cost = pieces * rate;

      if (!rate && pieces > 0) hasStitchingRateWarning = true;

      if (!stitchingByPart[garmentPart]) {
        stitchingByPart[garmentPart] = { totalPieces: 0, totalCost: 0, rateSet: false };
      }

      stitchingByPart[garmentPart].totalPieces += pieces;
      stitchingByPart[garmentPart].totalCost += cost;
      if (rate > 0) stitchingByPart[garmentPart].rateSet = true;

      stitchingCost += cost;
    });

    const stitchingCosts = Object.entries(stitchingByPart).map(([garmentPart, data]) => {
      const averageRate = data.totalPieces > 0 ? data.totalCost / data.totalPieces : 0;
      return {
        garmentPart,
        pieces: data.totalPieces,
        rate: Math.round(averageRate * 100) / 100,
        total: data.totalCost,
        rateSet: data.rateSet
      };
    });

    // ── Press Costs (consolidated by garment part, using quantity_received) ──
    const pressAssignments = stitchingData.filter((assignment: any) => assignment.jobType === 'PRESS');
    const pressByPart: { [key: string]: { totalPieces: number, totalCost: number, rateSet: boolean } } = {};
    let hasPressingRateWarning = false;

    pressAssignments.forEach((assignment: any) => {
      const garmentPart = assignment.garment_part || 'UNKNOWN';
      const receivedPieces = (assignment.receives || []).reduce(
        (sum: number, r: any) => sum + (r.quantity_received || 0) + (r.defect_quantity || 0), 0
      );
      const pieces = receivedPieces > 0 ? receivedPieces : (assignment.quantity_issued || 0);
      const rate = Number(assignment.stitching_rate) || 0;
      const cost = pieces * rate;

      if (!rate && pieces > 0) hasPressingRateWarning = true;

      if (!pressByPart[garmentPart]) {
        pressByPart[garmentPart] = { totalPieces: 0, totalCost: 0, rateSet: false };
      }

      pressByPart[garmentPart].totalPieces += pieces;
      pressByPart[garmentPart].totalCost += cost;
      if (rate > 0) pressByPart[garmentPart].rateSet = true;

      pressCost += cost;
    });

    const pressCosts = Object.entries(pressByPart).map(([garmentPart, data]) => {
      const averageRate = data.totalPieces > 0 ? data.totalCost / data.totalPieces : 0;
      return {
        garmentPart,
        pieces: data.totalPieces,
        rate: Math.round(averageRate * 100) / 100,
        total: data.totalCost,
        rateSet: data.rateSet
      };
    });

    // ── Job Work Costs ──
    // Exclude only internal stitching/press (already counted via worker assignments above).
    // All other job works (external, or internal non-stitching/press) are included.
    const jobWorkCosts = jobWorks
      .filter((jw: any) => {
        if (jw.isInternal) {
          const jt = (typeof jw.jobType === 'string' ? jw.jobType : '').toUpperCase();
          if (jt.includes('STITCHING') || jt.includes('PRESS')) return false;
        }
        return true;
      })
      .map((jw: any) => {
        const isCompleted = jw.status === 'COMPLETED';
        const isPending = jw.status !== 'COMPLETED' && jw.status !== 'CANCELLED';

        let cost = 0;
        let pieces = 0;
        let rate = 0;

        if (jw.processMaterial) {
          // Process-material job works: use receipt-based totals
          cost = jw.totalAmount || 0;
          pieces = jw.qty || 0;
          rate = jw.rate || 0;
        } else if (isCompleted) {
          // Completed: use design detail rate (issue rate) as primary — it's the agreed rate.
          // The backend's totalAmount uses receipt processingRate which may differ.
          if (Array.isArray(jw.designDetails) && jw.designDetails.length > 0) {
            const detailRate = jw.designDetails.find((d: any) => Number(d.rate) > 0)?.rate;
            if (detailRate) {
              rate = Number(detailRate);
              // Use received qty if available, otherwise issued qty from designDetails
              pieces = jw.qty || jw.designDetails.reduce((sum: number, d: any) => sum + (Number(d.pieces) || 0), 0);
              cost = pieces * rate;
            }
          }
          // Fallback to receipt-based amounts if designDetails have no rate
          if (cost <= 0) {
            cost = jw.totalAmount || 0;
            pieces = jw.qty || 0;
            rate = jw.rate || 0;
          }
        } else if (isPending) {
          // Issued / Pending: use issue qty × issue rate from designDetails
          if (Array.isArray(jw.designDetails) && jw.designDetails.length > 0) {
            pieces = jw.designDetails.reduce((sum: number, d: any) => sum + (Number(d.pieces) || 0), 0);
            const detailRate = jw.designDetails.find((d: any) => Number(d.rate) > 0)?.rate;
            rate = Number(detailRate) || 0;
            cost = pieces * rate;
          }
          // Fallback to job-work-level values
          if (cost <= 0) {
            pieces = jw.issuedQty || jw.qty || 0;
            rate = jw.rate || 0;
            cost = jw.totalAmount || (pieces * rate);
          }
        }

        // Final fallback for qty display
        if (pieces <= 0) pieces = jw.issuedQty || jw.qty || 0;

        jobWorkCost += cost;
        return {
          jobType: jw.jobType || 'Unknown',
          workerName: jw.jobWorkerName || 'Unknown',
          qty: pieces,
          rate,
          total: cost,
          processMaterial: jw.processMaterial || false,
          pending: isPending
        };
      });

    // ── Cost Per Piece ──
    // Prefer TOP cut pieces from design cutting; fallback to total issued pieces from job work design details
    let totalPiecesForCpp = topDesign?.total_pieces || 0;
    let piecesSource = 'TOP pieces cut';

    if (totalPiecesForCpp <= 0) {
      totalPiecesForCpp = jobWorks.reduce((sum: number, jw: any) => {
        if (jw.isInternal && Array.isArray(jw.designDetails)) {
          return sum + jw.designDetails.reduce((dSum: number, d: any) => dSum + (Number(d.pieces) || 0), 0);
        }
        return sum;
      }, 0);
      if (totalPiecesForCpp > 0) piecesSource = 'pieces issued (stitching)';
    }

    if (totalPiecesForCpp <= 0) {
      totalPiecesForCpp = jobWorks
        .filter((jw: any) => !jw.isInternal && !jw.processMaterial)
        .reduce((sum: number, jw: any) => sum + (jw.qty || 0), 0);
      if (totalPiecesForCpp > 0) piecesSource = 'pieces received (job work)';
    }

    const totalCost = materialCost + stitchingCost + pressCost + jobWorkCost;
    const costPerPiece = totalPiecesForCpp > 0 ? totalCost / totalPiecesForCpp : 0;

    return {
      materialCosts: materialCostsArr,
      stitchingCosts,
      pressCosts,
      jobWorkCosts,
      materialCost,
      stitchingCost,
      pressCost,
      jobWorkCost,
      totalCost,
      costPerPiece,
      totalTopPieces: totalPiecesForCpp,
      piecesSource,
      hasStitchingRateWarning,
      hasPressingRateWarning
    };
  };
  
  const costData = calculateCosts();
  const isMobile = !isTablet;

  // Status badge helper
  const statusBadge = (status: string, colorMap: Record<string, { bg: string; fg: string }>, fallback = { bg: 'grey.300', fg: 'text.primary' }) => {
    const c = colorMap[status] || fallback;
    return (
      <Box sx={{ px: 1, py: 0.5, borderRadius: 1, fontSize: '0.75rem', fontWeight: 600, display: 'inline-block', bgcolor: c.bg, color: c.fg }}>
        {status}
      </Box>
    );
  };

  const poStatusColors: Record<string, { bg: string; fg: string }> = {
    COMPLETED: { bg: 'success.light', fg: 'success.contrastText' }, CANCELLED: { bg: 'error.light', fg: 'error.contrastText' },
    ON_HOLD: { bg: 'warning.light', fg: 'warning.contrastText' }, DRAFT: { bg: 'grey.300', fg: 'text.primary' },
    CLOSED: { bg: 'success.light', fg: 'success.contrastText' }, SENT: { bg: 'info.light', fg: 'info.contrastText' },
    PENDING: { bg: 'warning.light', fg: 'warning.contrastText' },
  };
  const jwStatusColors: Record<string, { bg: string; fg: string }> = {
    COMPLETED: { bg: 'success.light', fg: 'success.contrastText' }, PENDING: { bg: 'warning.light', fg: 'warning.contrastText' },
  };

  // Section wrapper: Accordion on mobile, Paper card on desktop
  const renderSection = (title: string, content: React.ReactNode, opts?: { defaultExpanded?: boolean; badge?: React.ReactNode; hide?: boolean }) => {
    if (opts?.hide) return null;
    if (isMobile) {
      return (
        <Accordion defaultExpanded={opts?.defaultExpanded} disableGutters sx={{ '&:before': { display: 'none' }, mb: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: '4px !important', overflow: 'hidden' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 42, '& .MuiAccordionSummary-content': { my: 0.5, alignItems: 'center', gap: 1, flexWrap: 'wrap' } }}>
            <Typography fontWeight={700} fontSize="0.875rem" color="primary">{title}</Typography>
            {opts?.badge}
          </AccordionSummary>
          <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>{content}</AccordionDetails>
        </Accordion>
      );
    }
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary">{title}</Typography>
          {opts?.badge}
        </Box>
        {content}
      </Paper>
    );
  };

  // ── Size summary table renderer (reused for TOP/BOTTOM, STITCHING/PRESS) ──
  const renderSizeSummaryStitching = (data: SizeSummary[], label: string, color: string) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} color={color} sx={{ mb: 1 }}>{label} Stitching</Typography>
      {loadingSummary ? <CircularProgress size={20} /> : data.length > 0 ? (
        <TableContainer><Table size="small">
          <TableHead><TableRow sx={{ bgcolor: '#f5f5f5' }}>
            <TableCell sx={{ fontWeight: 'bold' }}>Size</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Cut</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Issued</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Defects</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Rcvd</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Rem</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Ref Iss</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Ref Rcvd</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {data.map(r => <TableRow key={r.size}><TableCell>{r.size}</TableCell><TableCell align="right">{r.totalCut}</TableCell><TableCell align="right">{r.stitchingIssued}</TableCell><TableCell align="right">{r.stitchingDefects}</TableCell><TableCell align="right">{r.stitchingReceived}</TableCell><TableCell align="right">{r.stitchingRemaining}</TableCell><TableCell align="right">{r.stitchingRefinish}</TableCell><TableCell align="right">{r.stitchingRefinishReceived}</TableCell></TableRow>)}
            <TableRow sx={{ bgcolor: '#f5f5f5' }}><TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>{[
              data.reduce((s,r) => s+r.totalCut,0), data.reduce((s,r) => s+r.stitchingIssued,0), data.reduce((s,r) => s+r.stitchingDefects,0),
              data.reduce((s,r) => s+r.stitchingReceived,0), data.reduce((s,r) => s+r.stitchingRemaining,0), data.reduce((s,r) => s+r.stitchingRefinish,0), data.reduce((s,r) => s+r.stitchingRefinishReceived,0)
            ].map((v,i) => <TableCell key={i} align="right" sx={{ fontWeight: 'bold' }}>{v}</TableCell>)}</TableRow>
          </TableBody>
        </Table></TableContainer>
      ) : <Typography variant="body2" color="text.secondary">No size data available</Typography>}
    </Box>
  );

  const renderSizeSummaryPress = (data: SizeSummary[], label: string, color: string) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} color={color} sx={{ mb: 1 }}>{label} Press</Typography>
      {loadingSummary ? <CircularProgress size={20} /> : data.length > 0 ? (
        <TableContainer><Table size="small">
          <TableHead><TableRow sx={{ bgcolor: '#f5f5f5' }}>
            <TableCell sx={{ fontWeight: 'bold' }}>Size</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Cut</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Issued</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Rcvd</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Rem</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {data.map(r => <TableRow key={r.size}><TableCell>{r.size}</TableCell><TableCell align="right">{r.totalCut}</TableCell><TableCell align="right">{r.pressIssued}</TableCell><TableCell align="right">{r.pressReceived}</TableCell><TableCell align="right">{r.pressRemaining}</TableCell></TableRow>)}
            <TableRow sx={{ bgcolor: '#f5f5f5' }}><TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>{[
              data.reduce((s,r) => s+r.totalCut,0), data.reduce((s,r) => s+r.pressIssued,0), data.reduce((s,r) => s+r.pressReceived,0), data.reduce((s,r) => s+r.pressRemaining,0)
            ].map((v,i) => <TableCell key={i} align="right" sx={{ fontWeight: 'bold' }}>{v}</TableCell>)}</TableRow>
          </TableBody>
        </Table></TableContainer>
      ) : null}
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
      {/* ── Header ── */}
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, px: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
          <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight={700} noWrap>{title}</Typography>
          {design?.product_type && <Chip label={design.product_type} size="small" variant="outlined" />}
          {design && (design.cad_file_1 || design.cad_file_2 || design.cad_file_3 || design.cad_file_4) && (
            <Chip label="CAD Done" size="small" color="success" />
          )}
        </Box>
        <IconButton aria-label="close" onClick={onClose} edge="end"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 1, sm: 2 } }}>
        {/* ── Notifications ── */}
        {statusError && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setStatusError(null)}>{statusError}</Alert>}

        {/* ══════════════════════════════════════════════
            TOP AREA: Image + Design Info (two columns on desktop)
           ══════════════════════════════════════════════ */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* Image Gallery */}
          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={{ p: 1, height: { xs: 300, md: 450 }, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', bgcolor: 'background.default', borderRadius: 2 }}>
              {images.length > 0 ? (
                <>
                  <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                    <img src={images[activeStep]} alt={`Design ${activeStep + 1}`} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    {images.length > 1 && (
                      <>
                        <IconButton sx={{ position: 'absolute', left: 4, bgcolor: 'rgba(255,255,255,0.7)', '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }} size="small" onClick={handleBack}><ArrowBackIcon fontSize="small" /></IconButton>
                        <IconButton sx={{ position: 'absolute', right: 4, bgcolor: 'rgba(255,255,255,0.7)', '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }} size="small" onClick={handleNext}><ArrowForwardIcon fontSize="small" /></IconButton>
                      </>
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ mt: 0.5 }}>
                    {activeStep + 1} / {images.length}
                    {activeStep === images.length - 1 && design?.dupatta_image_ref && ' (Dupatta Ref)'}
                  </Typography>
                </>
              ) : (
                <Typography color="text.secondary">No images available</Typography>
              )}
            </Paper>
            {/* Thumbnails */}
            {images.length > 1 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center', mt: 1 }}>
                {images.map((img, i) => (
                  <Box key={i} onClick={() => setActiveStep(i)} sx={{ width: 48, height: 48, border: i === activeStep ? '2px solid' : '1px solid', borderColor: i === activeStep ? 'primary.main' : 'divider', cursor: 'pointer', overflow: 'hidden', borderRadius: 0.5 }}>
                    <img src={img} alt={`Thumb ${i+1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                ))}
              </Box>
            )}
          </Grid>

          {/* Design Info + Approval */}
          <Grid item xs={12} md={7}>
            {design && (
              <Box>
                {/* Approval Status */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                  <Typography variant="body2" fontWeight={600}>Status:</Typography>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <Select value={approvalStatus} onChange={(e) => handleApprovalStatusChange(e.target.value as any)} disabled={updatingStatus} sx={{ fontSize: '0.875rem' }}>
                      <MenuItem value="APPROVED">Approved</MenuItem>
                      <MenuItem value="REJECTED">Rejected</MenuItem>
                      <MenuItem value="HOLD">Hold</MenuItem>
                      <MenuItem value="COMPLETE">Complete</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Compact Info Grid */}
                <Grid container spacing={1} sx={{ mb: 1 }}>
                  {[
                    { label: 'Design No', value: design.design_no },
                    { label: 'Designer', value: design.name || '-' },
                    { label: 'Product Type', value: design.product_type || '-' },
                    { label: 'Design Date', value: design.design_date ? new Date(design.design_date).toLocaleDateString('en-IN') : '-' },
                  ].map(({ label, value }) => (
                    <Grid item xs={6} sm={3} key={label}>
                      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                      <Typography variant="body2" fontWeight={500}>{value}</Typography>
                    </Grid>
                  ))}
                </Grid>

                {/* Description */}
                {design.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{design.description}</Typography>
                )}

                {/* Dupatta */}
                {design.product_type === 'SUIT' && design.dupatta_details && (
                  <Box sx={{ mb: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="caption" fontWeight={600}>Dupatta:</Typography>
                    <Typography variant="body2">{design.dupatta_details}</Typography>
                    {design.dupatta_rate && <Typography variant="body2">Rate: ₹{design.dupatta_rate.toFixed(2)}</Typography>}
                  </Box>
                )}
              </Box>
            )}

            {/* Production Summary — shown in right column on desktop */}
            {isTablet && renderSection('Production Summary', (
              batchesLoading ? <CircularProgress size={24} /> : summaryData ? (
                <Table size="small"><TableBody>
                  {[
                    { m: 'Inhouse Stitching Issued', v: summaryData.inhouseStitchingIssued },
                    { m: 'Cutting Quantity', v: summaryData.cuttingQty },
                    { m: 'Stitched Pieces', v: summaryData.stitchedPieces, color: 'success.main' },
                    { m: 'Stitching Defect', v: summaryData.stitchingDefects, color: 'error.main', prefix: '-' },
                    { m: 'Pressed Pieces', v: summaryData.pressedPieces, color: 'success.main' },
                    { m: 'Press Defect', v: summaryData.pressDefects, color: 'error.main', prefix: '-' },
                    { m: 'Barcoded', v: summaryData.barcoded },
                    { m: 'In Repair', v: summaryData.inRepair },
                    { m: 'Permanent Damage', v: summaryData.permanentDamage, color: 'error.main' },
                  ].map(({ m, v, color, prefix }) => (
                    <TableRow key={m}><TableCell sx={{ py: 0.5 }}>{m}</TableCell><TableCell align="right" sx={{ py: 0.5, color, fontWeight: color ? 500 : 400 }}>{prefix}{v}</TableCell></TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: 'action.hover' }}><TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Pending</TableCell><TableCell align="right" sx={{ fontWeight: 'bold', py: 0.5 }}>{summaryData.pending}</TableCell></TableRow>
                </TableBody></Table>
              ) : <Typography variant="body2" color="text.secondary">No production data available.</Typography>
            ), {
              badge: summaryData?.status ? (
                <Chip icon={summaryData.status === 'ok' ? <CheckCircleIcon /> : summaryData.status === 'needs_repair' ? <WarningIcon /> : <ErrorIcon />}
                  label={summaryData.statusMessage} color={summaryData.status === 'ok' ? 'success' : summaryData.status === 'needs_repair' ? 'warning' : 'error'} size="small" />
              ) : undefined
            })}
          </Grid>
        </Grid>

        {/* ══════════════════════════════════════════════
            SECTIONS (full width, Accordion on mobile)
           ══════════════════════════════════════════════ */}

        {/* Production Summary — mobile only (shown in right col on desktop) */}
        {isMobile && renderSection('Production Summary', (
          batchesLoading ? <CircularProgress size={24} /> : summaryData ? (
            <Table size="small"><TableBody>
              {[
                { m: 'Stitching Issued', v: summaryData.inhouseStitchingIssued },
                { m: 'Cutting Qty', v: summaryData.cuttingQty },
                { m: 'Stitched Pcs', v: summaryData.stitchedPieces, color: 'success.main' },
                { m: 'Stitch Defect', v: summaryData.stitchingDefects, color: 'error.main', prefix: '-' },
                { m: 'Pressed Pcs', v: summaryData.pressedPieces, color: 'success.main' },
                { m: 'Press Defect', v: summaryData.pressDefects, color: 'error.main', prefix: '-' },
                { m: 'Barcoded', v: summaryData.barcoded },
                { m: 'In Repair', v: summaryData.inRepair },
                { m: 'Perm Damage', v: summaryData.permanentDamage, color: 'error.main' },
              ].map(({ m, v, color, prefix }) => (
                <TableRow key={m}><TableCell sx={{ py: 0.5 }}>{m}</TableCell><TableCell align="right" sx={{ py: 0.5, color, fontWeight: color ? 500 : 400 }}>{prefix}{v}</TableCell></TableRow>
              ))}
              <TableRow sx={{ bgcolor: 'action.hover' }}><TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Pending</TableCell><TableCell align="right" sx={{ fontWeight: 'bold', py: 0.5 }}>{summaryData.pending}</TableCell></TableRow>
            </TableBody></Table>
          ) : <Typography variant="body2" color="text.secondary">No production data.</Typography>
        ), {
          defaultExpanded: true,
          badge: summaryData?.status ? (
            <Chip icon={summaryData.status === 'ok' ? <CheckCircleIcon /> : summaryData.status === 'needs_repair' ? <WarningIcon /> : <ErrorIcon />}
              label={summaryData.statusMessage} color={summaryData.status === 'ok' ? 'success' : summaryData.status === 'needs_repair' ? 'warning' : 'error'} size="small" />
          ) : undefined
        })}

        {/* Cost Breakdown */}
        {renderSection('Cost Breakdown', (
          (loadingMaterials || jobWorksLoading || stitchingLoading) ? <CircularProgress size={24} /> : (
            <>
              {(costData.hasStitchingRateWarning || costData.hasPressingRateWarning) && (
                <Alert severity="warning" sx={{ mb: 1, py: 0, fontSize: '0.8rem' }}>
                  Some {costData.hasStitchingRateWarning ? 'stitching' : ''}{costData.hasStitchingRateWarning && costData.hasPressingRateWarning ? ' / ' : ''}{costData.hasPressingRateWarning ? 'pressing' : ''} assignments have no rate set.
                </Alert>
              )}
              <TableContainer><Table size="small">
                <TableHead><TableRow>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell align="right"><strong>Qty</strong></TableCell>
                  <TableCell align="right"><strong>Rate</strong></TableCell>
                  <TableCell align="right"><strong>Total</strong></TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {costData.materialCosts.map((item: any, i: number) => (
                    <TableRow key={`m-${i}`}><TableCell>Material{item.source === 'Allocation' && <Typography variant="caption" display="block" color="text.secondary">Reserved</Typography>}</TableCell><TableCell>{item.type} ({item.code})</TableCell><TableCell align="right">{item.qty}</TableCell><TableCell align="right">₹{item.rate.toLocaleString('en-IN')}</TableCell><TableCell align="right">₹{item.total.toLocaleString('en-IN')}</TableCell></TableRow>
                  ))}
                  {costData.stitchingCosts.map((item: any, i: number) => (
                    <TableRow key={`s-${i}`}><TableCell>Stitching</TableCell><TableCell>{item.garmentPart}{!item.rateSet && item.pieces > 0 && <Typography variant="caption" display="block" color="warning.main">No rate</Typography>}</TableCell><TableCell align="right">{item.pieces} pcs</TableCell><TableCell align="right">₹{item.rate.toLocaleString('en-IN')}</TableCell><TableCell align="right">₹{item.total.toLocaleString('en-IN')}</TableCell></TableRow>
                  ))}
                  {costData.pressCosts.map((item: any, i: number) => (
                    <TableRow key={`p-${i}`}><TableCell>Pressing</TableCell><TableCell>{item.garmentPart}{!item.rateSet && item.pieces > 0 && <Typography variant="caption" display="block" color="warning.main">No rate</Typography>}</TableCell><TableCell align="right">{item.pieces} pcs</TableCell><TableCell align="right">₹{item.rate.toLocaleString('en-IN')}</TableCell><TableCell align="right">₹{item.total.toLocaleString('en-IN')}</TableCell></TableRow>
                  ))}
                  {costData.jobWorkCosts.map((item: any, i: number) => (
                    <TableRow key={`j-${i}`} sx={item.pending ? { bgcolor: '#ed6c02', '& td': { color: '#fff' } } : undefined}>
                      <TableCell>Job Work{item.pending && <Typography variant="caption" display="block" color="inherit" fontWeight={600}>Issued</Typography>}</TableCell>
                      <TableCell>{item.jobType} - {item.workerName}{item.processMaterial && <Typography variant="caption" display="block" color="inherit" sx={{ opacity: 0.85 }}>Processing</Typography>}</TableCell>
                      <TableCell align="right">{item.qty}</TableCell>
                      <TableCell align="right">₹{item.rate.toLocaleString('en-IN')}{item.pending && <Typography variant="caption" display="block" color="inherit" sx={{ opacity: 0.7, fontSize: '0.65rem' }}>Issue Rate</Typography>}</TableCell>
                      <TableCell align="right">₹{item.total.toLocaleString('en-IN')}{item.pending && <Typography variant="caption" display="block" color="inherit" sx={{ opacity: 0.85 }}>Est.</Typography>}</TableCell>
                    </TableRow>
                  ))}
                  {costData.materialCosts.length === 0 && costData.stitchingCosts.length === 0 && costData.pressCosts.length === 0 && costData.jobWorkCosts.length === 0 && (
                    <TableRow><TableCell colSpan={5} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No cost data yet</Typography></TableCell></TableRow>
                  )}
                  {/* Subtotals */}
                  {costData.materialCost > 0 && <TableRow sx={{ bgcolor: 'action.hover' }}><TableCell><strong>Material</strong></TableCell><TableCell colSpan={3} /><TableCell align="right"><strong>₹{costData.materialCost.toLocaleString('en-IN')}</strong></TableCell></TableRow>}
                  {costData.stitchingCost > 0 && <TableRow sx={{ bgcolor: 'action.hover' }}><TableCell><strong>Stitching</strong></TableCell><TableCell colSpan={3} /><TableCell align="right"><strong>₹{costData.stitchingCost.toLocaleString('en-IN')}</strong></TableCell></TableRow>}
                  {costData.pressCost > 0 && <TableRow sx={{ bgcolor: 'action.hover' }}><TableCell><strong>Press</strong></TableCell><TableCell colSpan={3} /><TableCell align="right"><strong>₹{costData.pressCost.toLocaleString('en-IN')}</strong></TableCell></TableRow>}
                  {costData.jobWorkCost > 0 && <TableRow sx={{ bgcolor: 'action.hover' }}><TableCell><strong>Job Work</strong></TableCell><TableCell colSpan={3} /><TableCell align="right"><strong>₹{costData.jobWorkCost.toLocaleString('en-IN')}</strong></TableCell></TableRow>}
                  <TableRow sx={{ bgcolor: 'primary.light' }}><TableCell><strong>TOTAL</strong></TableCell><TableCell colSpan={3} /><TableCell align="right"><Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>₹{costData.totalCost.toLocaleString('en-IN')}</Typography></TableCell></TableRow>
                  {costData.totalTopPieces > 0 && (
                    <TableRow sx={{ bgcolor: 'success.light' }}><TableCell><strong>PER PIECE</strong></TableCell><TableCell colSpan={2}><Typography variant="caption">{costData.totalTopPieces} {costData.piecesSource}</Typography></TableCell><TableCell align="right"><Typography sx={{ fontWeight: 'bold', color: 'success.contrastText' }}>₹{costData.costPerPiece.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography></TableCell><TableCell /></TableRow>
                  )}
                </TableBody>
              </Table></TableContainer>
            </>
          )
        ), { hide: hideCost || !canViewCost || (!design && !designNo), badge: costData.totalCost > 0 ? <Chip label={`₹${costData.totalCost.toLocaleString('en-IN')}`} size="small" color="primary" /> : undefined })}

        {/* BOM */}
        {design && renderSection('Bill of Materials', (
          bomLoading ? <CircularProgress size={24} /> : bomItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No BOM items.</Typography>
          ) : (
            <TableContainer><Table size="small">
              <TableHead><TableRow><TableCell>Material</TableCell><TableCell>Part</TableCell><TableCell align="right">Per Pc</TableCell><TableCell>UOM</TableCell><TableCell align="right">Rate</TableCell><TableCell align="right">Wastage%</TableCell></TableRow></TableHead>
              <TableBody>
                {bomItems.map(item => (
                  <TableRow key={item.id}><TableCell><Typography variant="body2" fontWeight={500}>{item.material_name}</Typography><Typography variant="caption" color="text.secondary">{item.material_type}</Typography></TableCell><TableCell>{item.garment_part}</TableCell><TableCell align="right">{item.consumption_per_piece}</TableCell><TableCell>{item.uom}</TableCell><TableCell align="right">{item.estimated_rate ? `₹${item.estimated_rate}` : '-'}</TableCell><TableCell align="right">{item.wastage_pct}%</TableCell></TableRow>
                ))}
                {bomEstimatedCost > 0 && <TableRow><TableCell colSpan={4} align="right"><Typography variant="subtitle2">Est. BOM Cost</Typography></TableCell><TableCell align="right"><Typography variant="subtitle2" color="primary">₹{bomEstimatedCost.toFixed(2)}</Typography></TableCell><TableCell /></TableRow>}
              </TableBody>
            </Table></TableContainer>
          )
        ), { badge: <>{bomItems.length > 0 && <Chip label={`${bomItems.length} items`} size="small" />}{isBomFinalized && <Chip label="Finalized" color="success" size="small" />}</> })}

        {/* Materials Allocated */}
        {(design || designNo) && renderSection('Materials Allocated', (
          loadingMaterials ? <CircularProgress size={24} /> : materialsError ? (
            <Alert severity="error">{materialsError}</Alert>
          ) : allocatedMaterials.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No materials allocated.</Typography>
          ) : (
            <TableContainer><Table size="small">
              <TableHead><TableRow>
                <TableCell><strong>Code</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                {isTablet && <TableCell><strong>Quality</strong></TableCell>}
                <TableCell><strong>Part</strong></TableCell>
                <TableCell align="right"><strong>Qty</strong></TableCell>
                <TableCell align="right"><strong>Rate</strong></TableCell>
                {isTablet && <TableCell><strong>Image</strong></TableCell>}
              </TableRow></TableHead>
              <TableBody>
                {allocatedMaterials.map(mat => (
                  <TableRow key={mat.id}>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {mat.material?.material_code ? (
                        <Button variant="text" color="primary" size="small" onClick={() => window.open(`/inventory?q=${encodeURIComponent(mat.material?.material_code || '')}`, '_blank')} sx={{ p: 0, minWidth: 0, textTransform: 'none', fontWeight: 500 }}>{mat.material.material_code}</Button>
                      ) : '-'}
                    </TableCell>
                    <TableCell>{mat.material?.material_type || '-'}</TableCell>
                    {isTablet && <TableCell>{mat.material?.material_quality || '-'}</TableCell>}
                    <TableCell>{mat.garment_part}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{mat.allocated_qty}</TableCell>
                    <TableCell align="right">₹{mat.material?.rate?.toLocaleString('en-IN') || '0'}</TableCell>
                    {isTablet && <TableCell>{mat.material?.image_url ? <img src={mat.material.image_url} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} /> : '-'}</TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table></TableContainer>
          )
        ), { badge: allocatedMaterials.length > 0 ? <Chip label={`${allocatedMaterials.length}`} size="small" /> : undefined })}

        {/* Design Cutting & Size Summaries */}
        {(designNo || topDesign || bottomDesign || loadingCuttingData) && renderSection('Design Cutting & Sizes', (
          <>
            {loadingCuttingData ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}><CircularProgress size={20} /><Typography variant="body2" color="text.secondary">Loading...</Typography></Box>
            ) : !topDesign && !bottomDesign && designNo ? (
              <Typography variant="body2" color="text.secondary">No cutting data for {designNo}</Typography>
            ) : (
              <>
                {/* Cutting Info Table */}
                {(topDesign || bottomDesign) && (
                  <Table size="small" sx={{ mb: 2 }}><TableBody>
                    {topDesign && (
                      <>
                        <TableRow><TableCell colSpan={2} sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>TOP</TableCell></TableRow>
                        <TableRow><TableCell sx={{ fontWeight: 600 }}>Pieces</TableCell><TableCell>{topDesign.total_pieces || '-'}</TableCell></TableRow>
                        <TableRow><TableCell sx={{ fontWeight: 600 }}>Fabric (m)</TableCell><TableCell>{topDesign.fabric_meter || '-'}</TableCell></TableRow>
                        <TableRow><TableCell sx={{ fontWeight: 600 }}>Received</TableCell><TableCell>{topDesign.received_date ? new Date(topDesign.received_date).toLocaleDateString('en-IN') : '-'}</TableCell></TableRow>
                      </>
                    )}
                    {bottomDesign && (
                      <>
                        <TableRow><TableCell colSpan={2} sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>BOTTOM</TableCell></TableRow>
                        <TableRow><TableCell sx={{ fontWeight: 600 }}>Pieces</TableCell><TableCell>{bottomDesign.total_pieces || '-'}</TableCell></TableRow>
                        <TableRow><TableCell sx={{ fontWeight: 600 }}>Fabric (m)</TableCell><TableCell>{bottomDesign.fabric_meter || '-'}</TableCell></TableRow>
                        <TableRow><TableCell sx={{ fontWeight: 600 }}>Received</TableCell><TableCell>{bottomDesign.received_date ? new Date(bottomDesign.received_date).toLocaleDateString('en-IN') : '-'}</TableCell></TableRow>
                      </>
                    )}
                  </TableBody></Table>
                )}

                {/* Size Summaries */}
                {topDesign && (
                  <>
                    {renderSizeSummaryStitching(topSizeSummary, 'TOP', 'primary.main')}
                    {renderSizeSummaryPress(topSizeSummary, 'TOP', 'primary.main')}
                  </>
                )}
                {bottomDesign && (
                  <>
                    {renderSizeSummaryStitching(bottomSizeSummary, 'BOTTOM', 'secondary.main')}
                    {renderSizeSummaryPress(bottomSizeSummary, 'BOTTOM', 'secondary.main')}
                  </>
                )}
              </>
            )}
          </>
        ))}

        {/* Production Orders */}
        {renderSection('Production Orders', (
          productionOrdersLoading ? <CircularProgress size={24} /> : productionOrders.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No production orders.</Typography>
          ) : (
            <TableContainer><Table size="small">
              <TableHead><TableRow>
                <TableCell><strong>Order #</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Plan</strong></TableCell>
                <TableCell align="right"><strong>Cut</strong></TableCell>
                {isTablet && <><TableCell align="right"><strong>Stitch</strong></TableCell><TableCell align="right"><strong>Finish</strong></TableCell></>}
                <TableCell><strong>Target</strong></TableCell>
                <TableCell><strong>Priority</strong></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {productionOrders.map(po => (
                  <TableRow key={po.id}>
                    <TableCell><Button variant="text" color="primary" size="small" onClick={() => navigate(`/production-orders/${po.id}`)} sx={{ p: 0, minWidth: 0, textTransform: 'none' }}>{po.order_number}</Button></TableCell>
                    <TableCell>{statusBadge(po.status, poStatusColors)}</TableCell>
                    <TableCell align="right">{po.total_planned}</TableCell>
                    <TableCell align="right">{po.total_cut}</TableCell>
                    {isTablet && <><TableCell align="right">{po.total_stitched}</TableCell><TableCell align="right">{po.total_finished}</TableCell></>}
                    <TableCell>{po.target_date ? new Date(po.target_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-'}</TableCell>
                    <TableCell>{statusBadge(po.priority, { URGENT: { bg: 'error.light', fg: 'error.contrastText' }, HIGH: { bg: 'warning.light', fg: 'warning.contrastText' } }, { bg: 'grey.200', fg: 'text.secondary' })}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></TableContainer>
          )
        ), { badge: productionOrders.length > 0 ? <Chip label={`${productionOrders.length}`} size="small" /> : undefined })}

        {/* Purchase Orders */}
        {renderSection('Purchase Orders', (
          purchaseOrdersLoading ? <CircularProgress size={24} /> : purchaseOrders.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No purchase orders.</Typography>
          ) : (
            <TableContainer><Table size="small">
              <TableHead><TableRow>
                <TableCell><strong>PO #</strong></TableCell>
                <TableCell><strong>Vendor</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                {isTablet && <><TableCell align="right"><strong>Items</strong></TableCell><TableCell><strong>Created</strong></TableCell><TableCell><strong>Valid Until</strong></TableCell></>}
              </TableRow></TableHead>
              <TableBody>
                {purchaseOrders.map((po: any) => (
                  <TableRow key={po.id}>
                    <TableCell><Button variant="text" color="primary" size="small" onClick={() => navigate(`/purchase-orders?po=${po.po_number}`)} sx={{ p: 0, minWidth: 0, textTransform: 'none' }}>{po.po_number}</Button></TableCell>
                    <TableCell>{po.vendor?.registered_name || po.vendor?.alias || '-'}</TableCell>
                    <TableCell>{statusBadge(po.status, poStatusColors)}</TableCell>
                    {isTablet && (
                      <>
                        <TableCell align="right">{po.items?.length || po._count?.purchases || 0}</TableCell>
                        <TableCell>{new Date(po.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}</TableCell>
                        <TableCell>{po.valid_until ? new Date(po.valid_until).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '-'}</TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table></TableContainer>
          )
        ), { badge: purchaseOrders.length > 0 ? <Chip label={`${purchaseOrders.length}`} size="small" /> : undefined })}

        {/* Job Works */}
        {renderSection('Job Works', (
          jobWorksLoading ? <CircularProgress size={24} /> : jobWorks.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No job work records.</Typography>
          ) : (
            <TableContainer><Table size="small">
              <TableHead><TableRow>
                <TableCell><strong>Code</strong></TableCell>
                {isTablet && <TableCell><strong>Date</strong></TableCell>}
                <TableCell><strong>Worker</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell align="right"><strong>Qty</strong></TableCell>
                {isTablet && <TableCell align="right"><strong>Rate</strong></TableCell>}
                <TableCell align="right"><strong>Amount</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {jobWorks.map((jw: any, i: number) => (
                  <TableRow key={i}>
                    <TableCell><Button variant="text" color="primary" size="small" onClick={() => navigate(`/job-works/${jw.id}?returnTo=/designs`)} sx={{ p: 0, minWidth: 0, textTransform: 'none' }}>{jw.jobWorkCode}</Button></TableCell>
                    {isTablet && <TableCell>{jw.jobWorkDate ? new Date(jw.jobWorkDate).toLocaleDateString('en-IN') : '-'}</TableCell>}
                    <TableCell>{jw.jobWorkerName}</TableCell>
                    <TableCell><Box sx={{ px: 0.75, py: 0.25, borderRadius: 1, bgcolor: 'primary.light', color: 'primary.contrastText', fontSize: '0.7rem', fontWeight: 500, display: 'inline-block' }}>{jw.jobType}</Box></TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{jw.qty}</TableCell>
                    {isTablet && <TableCell align="right">₹{jw.rate?.toLocaleString('en-IN') || '0'}</TableCell>}
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'success.main' }}>₹{jw.totalAmount?.toLocaleString('en-IN') || '0'}</TableCell>
                    <TableCell>{statusBadge(jw.status, jwStatusColors, { bg: 'info.light', fg: 'info.contrastText' })}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></TableContainer>
          )
        ), { badge: jobWorks.length > 0 ? <Chip label={`${jobWorks.length}`} size="small" /> : undefined })}

      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1 }}>
        <Button onClick={onClose} variant="contained" size="small">Close</Button>
      </DialogActions>

      <Snackbar open={!!statusSuccess} autoHideDuration={6000} onClose={() => setStatusSuccess(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setStatusSuccess(null)} severity="success" sx={{ width: '100%' }}>{statusSuccess}</Alert>
      </Snackbar>
      <Snackbar open={!!statusError} autoHideDuration={6000} onClose={() => setStatusError(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setStatusError(null)} severity="error" sx={{ width: '100%' }}>{statusError}</Alert>
      </Snackbar>
    </Dialog>
  );
};

export default DesignPreviewDialog; 